import React, { useRef, useState } from "react";
import { UploadCloud, Loader2, X } from "lucide-react";
import { api, assetUrl } from "@/lib/api";
import { toast } from "sonner";

export default function ImageUpload({ value, onChange, label = "Image", testid = "image-upload", aspect = "aspect-square", append = false }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  if (append) {
    return (
      <div>
        {label && <label className="text-xs text-espresso/60 mb-1 block">{label}</label>}
        <input ref={inputRef} type="file" accept="image/*" onChange={handle} className="hidden" data-testid={`${testid}-input`} />
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 bg-espresso text-ivory px-4 py-2 rounded-full text-xs disabled:opacity-60" data-testid={`${testid}-btn`}>
          {uploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading...</> : <><UploadCloud className="w-3.5 h-3.5" /> Upload & Add Image</>}
        </button>
      </div>
    );
  }

  return (
    <div>
      {label && <label className="text-xs text-espresso/60 mb-1 block">{label}</label>}
      <div className="flex items-center gap-4">
        <div className={`relative w-24 ${aspect} rounded-xl overflow-hidden bg-beige border border-champagne/30 shrink-0 flex items-center justify-center`}>
          {value ? (
            <>
              <img src={assetUrl(value)} alt="preview" className="w-full h-full object-cover" />
              <button type="button" onClick={() => onChange("")} className="absolute top-1 right-1 w-5 h-5 bg-espresso/70 rounded-full flex items-center justify-center"><X className="w-3 h-3 text-ivory" /></button>
            </>
          ) : (
            <UploadCloud className="w-6 h-6 text-champagne/60" />
          )}
        </div>
        <div className="flex-1">
          <input ref={inputRef} type="file" accept="image/*" onChange={handle} className="hidden" data-testid={`${testid}-input`} />
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 bg-espresso text-ivory px-4 py-2 rounded-full text-xs disabled:opacity-60" data-testid={`${testid}-btn`}>
            {uploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading...</> : <><UploadCloud className="w-3.5 h-3.5" /> Upload Image</>}
          </button>
          <p className="text-[11px] text-espresso/40 mt-1.5">Or paste an image URL below</p>
          <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://..." className="w-full mt-1 border border-espresso/20 rounded-lg px-3 py-1.5 bg-ivory outline-none focus:border-champagne text-xs" data-testid={`${testid}-url`} />
        </div>
      </div>
    </div>
  );
}
